import OfflineBanner from "./OfflineBanner";
import TimezoneNotice from "./TimezoneNotice";
import UpdateBanner from "./UpdateBanner";
import WhatsNewDialog from "./WhatsNewDialog";
import VersionBadge from "./VersionBadge";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PageErrorBoundary from "./PageErrorBoundary";
import { useAuth } from "../contexts/AuthContext";
import { SETTINGS_ACCESS, TRASH_ACCESS, hasPermission } from "../lib/permissions";
import {
  LayoutDashboard,
  KanbanSquare,
  Users,
  UserCog,
  Lock,
  CheckSquare,
  Upload,
  History,
  Settings as SettingsIcon,
  ShieldCheck,
  UsersRound,
  Wallet,
  Headset,
  MapPinned,
  Printer,
  Database,
  GraduationCap,
  Store,
  LifeBuoy,
  Gauge,
  Tags,
  ReceiptText,
  BookOpen,
  Library,
  Lightbulb,
  Rocket, AlertTriangle,
  Laptop, Truck, PackageCheck, ShoppingCart, ClipboardList,
  FileText,
  CalendarClock,
  FolderOpen,
  FolderCheck,
  FileStack,
  Palette,
  BookText,
  Megaphone,
  Handshake,
  BarChart3,
  Landmark,
  Scale,
  Gavel,
  CalendarDays,
  ShieldAlert,
  Briefcase,
  Coins,
  ArrowLeft,
  MessagesSquare,
  Package,
  ScrollText,
  Compass,
  KeyRound,
  Star,
  ChevronRight,
  ChevronDown,
  Building2,
  Inbox,
  Network,
  MapPin,
  Award,
  SignalHigh,
  Trash2,
} from "lucide-react";
import { CommercialIcon, CustomersIcon, RevenueIcon, SupportIcon, ProductIcon, DocumentsIcon, FieldIcon, PurchasesIcon, FinanceIcon, AdminIcon, MarketingIcon, GovernanceIcon, CockpitIcon, TeamIcon, JamoraAppIcon, LauncherIcon } from "./icons/AppIcons";
import CopilotLogo from "./copilot/CopilotLogo";
import { ORG_BASE, orgPath, visibleOrgSections } from "../lib/collaborators";
import api from "../lib/api";
import useVisiblePolling from "../hooks/useVisiblePolling";
import { NAV_BADGE_POLL_MS, QUEUE_CHANGED_EVENT, canOpenAssist, canUseAssist } from "../lib/assist";
import { MISSION_READ_KEYS } from "../lib/missions";
import { CUSTOMER_READ_KEYS } from "../lib/customers";
import { PRODUCT_CREATE_KEYS, PRODUCT_READ_KEYS } from "../lib/product";
import { TICKETS_BADGE_POLL_MS, TICKETS_CHANGED_EVENT, TICKETS_QUEUE_PARAMS, TICKET_READ_KEYS, canSeeTicketQueue } from "../lib/tickets";
import { canSeeBilling } from "../lib/billing";
import { canSeeInventory, canSeeOwnAssets, canSeeRequests, canSeeSuppliers } from "../lib/procurement";
import { canSeeMarketing } from "../lib/marketing";
import { BASE as JAMORA_ADMIN_BASE, canOpen as canOpenJamoraAdmin, canViewLogs as canViewJamoraLogs, canViewPharmacy as canViewJamoraPharmacy, canViewPlatform as canViewJamoraPlatform } from "../lib/jamoraAdmin";
import { canSeeGovernance } from "../lib/governance";
import { canReadKnowledge } from "../lib/knowledge";
import { canSeeRecords } from "../lib/records";
import { EQUIPMENT_READ_KEYS } from "../lib/equipment";
import { CATALOG_READ_KEYS } from "../lib/training";
import { canManageProcedures } from "../lib/procedures";
import {
  arrivalMessage, claimAlert, newArrivals, playArrivalSound, showSystemNotification, titleWithCount,
} from "../lib/assistAlerts";
import NotificationsBell from "./NotificationsBell";
import GlobalSearch from "./GlobalSearch";
import QuickActionsMenu from "./QuickActionsMenu";
import UserMenu from "./UserMenu";
import MobileBottomNav from "./MobileBottomNav";
import InstallButton from "./InstallButton";
import Copilot from "./copilot/Copilot";
import { CopilotProvider } from "./copilot/CopilotSession";
import { COPILOT_PAGE, canOfferCopilot } from "../lib/copilot";
import { UPCOMING_LABEL, matchesPath } from "../lib/navigation";
import { appIdOfPath, breadcrumbOf, buildApps, entryIndex, resolveRecents } from "../lib/apps";
import useNavMemory from "../hooks/useNavMemory";
import useAppCounts from "../hooks/useAppCounts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import logo from "../assets/jamora-crm-logo-white.png";

// The menu follows the domains planned by the V2 specification (§ 14.1). Each entry keeps its own rule (permission and/or
// module switch, decided in Layout below); the planned modules not built yet are listed greyed (`upcoming`), never clickable.
const NAV_DASHBOARD = { id: "dashboard", to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" };
const NAV_PIPELINE = { id: "pipeline", to: "/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline", permission: "prospects:view" };
const NAV_PROSPECTS = { id: "prospects", to: "/prospects", label: "Prospects", icon: Users, testid: "nav-prospects", permission: "prospects:view" };
const NAV_TASKS = { id: "tasks", to: "/tasks", label: "Tâches", icon: CheckSquare, testid: "nav-tasks", permission: "tasks:view" };

// The centre of applications (a route of its own, not an entry of any application).
const APPS_PAGE = "/apps";
// The applications launcher of the sidebar: a tinted button (filled when the centre is open) that stands apart from the page entries.
const launcherClass = (active) => `flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${active
  ? "border-teal-300 bg-teal-400 text-jamora" : "border-teal-400/30 bg-teal-400/15 text-teal-50 hover:bg-teal-400/25"}`;
const VISIT_DWELL_MS = 2000;

const upcoming = (id, label, icon) => ({ id, label, icon, upcoming: true, testid: `nav-upcoming-${id}` });

// Number of requests waiting in the queue; null when unknown (never a false 0). Read even while the tab is hidden, so
// that a NEW request is announced wherever the technician is: sound, system notification (if allowed), toast, and
// the count in the tab title. Requests already waiting when the CRM opens are not announced (the badge shows them).
function useAssistQueueCount(enabled) {
  const navigate = useNavigate();
  const [count, setCount] = useState(null);
  const knownIds = useRef(null);
  const refresh = useCallback(() => {
    api.get("/assist/requests").then(({ data }) => {
      const arrivals = newArrivals(knownIds.current, data);
      knownIds.current = new Set(data.map((item) => item.id));
      setCount(data.length);
      const fresh = arrivals.filter((item) => claimAlert(item.id));
      if (!fresh.length) return;
      const open = () => navigate("/assistance");
      playArrivalSound();
      fresh.forEach((item) => showSystemNotification(item, open));
      toast(arrivalMessage(fresh), { action: { label: "Voir", onClick: open } });
    }).catch(() => setCount(null));
  }, [navigate]);
  useEffect(() => {
    if (!enabled) {
      setCount(null);
      knownIds.current = null;
      return undefined;
    }
    refresh();
    window.addEventListener(QUEUE_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(QUEUE_CHANGED_EVENT, refresh);
  }, [enabled, refresh]);
  useEffect(() => {
    document.title = titleWithCount(enabled ? count || 0 : 0);
  }, [enabled, count]);
  useVisiblePolling(refresh, NAV_BADGE_POLL_MS, enabled, NAV_BADGE_POLL_MS);
  return count;
}

// Open tickets nobody has taken yet that the person sees (the queue to take charge of); null when unknown, never a false 0.
function useTicketQueueCount(enabled) {
  const [count, setCount] = useState(null);
  const refresh = useCallback(() => {
    api.get("/tickets", { params: TICKETS_QUEUE_PARAMS }).then(({ data }) => setCount(data.total)).catch(() => setCount(null));
  }, []);
  useEffect(() => {
    if (!enabled) {
      setCount(null);
      return undefined;
    }
    refresh();
    window.addEventListener(TICKETS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(TICKETS_CHANGED_EVENT, refresh);
  }, [enabled, refresh]);
  useVisiblePolling(refresh, TICKETS_BADGE_POLL_MS, enabled);
  return count;
}

const NavBadge = ({ count, label = "en attente", testid = "nav-assist-badge" }) => (count > 0 ? (
  <span className="ml-auto rounded-full bg-amber-400 px-1.5 text-2xs font-semibold text-jamora" aria-label={`${count} ${label}`} data-testid={testid}>
    {count}
  </span>
) : null);

// Icons of the Organisation sub-menu entries (the team's referentials).
const ORG_ICONS = { services: Network, zones: MapPin, "types-de-relation": Handshake, "fonctions-metier": Briefcase, competences: Award, niveaux: SignalHigh,
  "types-de-mission": ClipboardList, "modeles-compte-rendu": FileText, "types-de-mandat": Scale, controle: ShieldAlert };

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-3 pl-2.5 pr-3 py-2.5 rounded-lg text-sm border-l-2 transition ${
    isActive
      ? "bg-white/10 border-teal-400 text-white font-medium"
      : "border-transparent text-white/70 hover:text-white hover:bg-white/5"
  }`;

// A planned module: greyed, not a link; hovering or focusing it says it is not available yet.
function UpcomingEntry({ item }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} role="link" aria-disabled="true" data-testid={item.testid}
          className="flex items-center gap-3 pl-2.5 pr-3 py-2.5 rounded-lg text-sm border-l-2 border-transparent text-white/35 cursor-not-allowed select-none">
          <item.icon size={18} />
          {item.label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right">{UPCOMING_LABEL}</TooltipContent>
    </Tooltip>
  );
}

function NavEntry({ item }) {
  const { pathname } = useLocation();
  if (item.upcoming) return <UpcomingEntry item={item} />;
  return (
    // `aria-current` follows the same rule as the highlight: the router alone would also mark « Annuaire » on its sub-pages (`except`).
    <NavLink to={item.to} data-testid={item.testid} aria-current={matchesPath(item, pathname) ? "page" : "false"}
      className={() => navLinkClass({ isActive: matchesPath(item, pathname) })}>
      {item.icon === CopilotLogo ? <CopilotLogo variant="onDark" size={18} /> : <item.icon size={18} />}
      {item.label}
      <NavBadge count={item.badge} label={item.badgeLabel} testid={item.badgeTestid} />
    </NavLink>
  );
}

// A sub-menu: the entries of an application that share a `group` fold under its title (open while one of them is the current page).
function NavGroup({ label, icon: Icon, items }) {
  const { pathname } = useLocation();
  const active = items.some((item) => !item.upcoming && matchesPath(item, pathname));
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div data-testid={`nav-group-${id}`}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)} data-testid={`nav-group-${id}-toggle`}
        className={`${navLinkClass({ isActive: false })} w-full text-left`}>
        {Icon && <Icon size={18} />}
        <span className="flex-1">{label}</span>
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="ml-5 mt-1 pl-2 border-l border-white/15 space-y-1" data-testid={`nav-group-${id}-items`}>
          {items.map((item) => <NavEntry key={item.id} item={item} />)}
        </div>
      )}
    </div>
  );
}

// An application's entries, the grouped ones under their sub-menu.
function NavItems({ items }) {
  const blocks = [];
  for (const item of items) {
    const last = blocks[blocks.length - 1];
    if (item.group && last?.group === item.group) last.items.push(item);
    else blocks.push({ group: item.group, icon: item.groupIcon, items: [item] });
  }
  return blocks.map((block) => (block.group
    ? <NavGroup key={`group-${block.group}`} label={block.group} icon={block.icon} items={block.items} />
    : <NavEntry key={block.items[0].id} item={block.items[0]} />));
}

// A favourite application in the sidebar (only reachable applications are favourites).
function AppLink({ app }) {
  return (
    <NavLink to={app.to} data-testid={`nav-app-${app.id}`} className={navLinkClass({ isActive: false })}>
      <app.icon size={18} />
      {app.label}
      <NavBadge count={app.badge} testid={`nav-app-${app.id}-badge`} />
    </NavLink>
  );
}

export default function Layout() {
  const { user } = useAuth();
  // The Copilote's own page fills the whole area under the header, with its own scrolling (no page padding).
  const { pathname } = useLocation();
  const fullBleed = pathname === COPILOT_PAGE;

  const can = (key) => hasPermission(user, key);
  const on = (feature) => !!user?.features?.[feature];
  const isAdmin = user?.role === "admin";
  // The planned modules are listed for administrators only: they follow the roadmap; nobody else gains anything from greyed entries.
  const showUpcoming = isAdmin;

  const ticketQueueEnabled = canSeeTicketQueue(user);
  const ticketCount = useTicketQueueCount(ticketQueueEnabled);
  // The queue badge and its alerts are the technicians' (support:handle); a supervisor who only reads history and reports
  // (support:view_all) gets the menu entry without them.
  const assistEnabled = canUseAssist(user);
  const assistCount = useAssistQueueCount(assistEnabled);
  // Someone who only writes procedures (procedures:manage, no mission right) still reaches them from the same entry.
  const missionsReadable = can(MISSION_READ_KEYS) && on("missions");
  const financeVisible = on("finance") && ["finance:view_summary", "finance:view_all", "finance:view_own_contributions", "finance:view_contributions",
    "finance:create_expense", "finance:manage_subscriptions", "finance:submit_claims", "finance:approve_claims"].some(can);
  const soon = (id, label, icon) => ({ ...upcoming(id, label, icon), visible: showUpcoming });

  const sections = [
    { id: "top", flat: true, items: [
      { ...NAV_DASHBOARD, visible: true },
      // Right under the dashboard (user's request 2026-10-23). Real once the module is on (and the person may open it); until then
      // administrators still see it as planned.
      ...(on("cockpit")
        ? [{ id: "cockpit", to: "/cockpit", label: "Cockpit de direction", icon: Gauge, testid: "nav-cockpit", visible: can("analytics:view_company") }]
        : [soon("cockpit", "Cockpit de direction", Gauge)]),
      { ...NAV_TASKS, visible: can(NAV_TASKS.permission) },
      { id: "copilot", to: COPILOT_PAGE, label: "Copilote", icon: CopilotLogo, testid: "nav-copilot", visible: canOfferCopilot(user) },
    ] },
    { id: "commercial", label: "Commercial", icon: CommercialIcon, accent: "blue", description: "Pipeline, prospects, pilotes et imports.", items: [
      { ...NAV_PIPELINE, visible: can(NAV_PIPELINE.permission) },
      { ...NAV_PROSPECTS, visible: can(NAV_PROSPECTS.permission) },
      { id: "pilots", to: "/pilotes", label: "Pilotes", icon: ShieldCheck, testid: "nav-pilots", visible: can("prospects:view") },
      { id: "import", to: "/import", label: "Import CSV", icon: Upload, testid: "nav-import", visible: can("csv:import") },
    ] },
    { id: "customers", label: "Pharmacies clientes", icon: CustomersIcon, accent: "teal", description: "Dossiers clients, santé et suivi.", items: [
      { id: "customers", to: "/clients", label: "Clients", icon: Store, testid: "nav-customers", visible: can(CUSTOMER_READ_KEYS) && on("customers") },
    ] },
    // Revenus (Ordre 17): offers and customer subscriptions (the subscriptions list also opens the offers tab), installments and payments.
    { id: "revenue", label: "Offres et revenus", icon: RevenueIcon, accent: "emerald", description: "Offres, abonnements, factures et encaissements.", items: [
      { id: "offers", to: "/revenus/abonnements", also: ["/revenus/offres"], label: "Offres et abonnements", icon: Tags, testid: "nav-billing-subscriptions",
        visible: canSeeBilling(user) },
      { id: "invoices", to: "/revenus/factures", label: "Factures et échéances", icon: ReceiptText, testid: "nav-billing-invoices", visible: canSeeBilling(user) },
    ] },
    // Tickets support (Ordre 08) next to the remote assistance; both badges count what waits for someone to take it.
    { id: "support", label: "Support", icon: SupportIcon, accent: "orange", description: "Tickets et assistance à distance.", items: [
      { id: "tickets", to: "/support/tickets", label: "Tickets", icon: LifeBuoy, testid: "nav-tickets", visible: can(TICKET_READ_KEYS) && on("tickets"),
        badge: ticketQueueEnabled ? ticketCount : null, badgeLabel: "sans responsable", badgeTestid: "nav-tickets-badge" },
      { id: "assist", to: "/assistance", label: "Assistance", icon: Headset, testid: "nav-assist", visible: canOpenAssist(user),
        badge: assistEnabled ? assistCount : null },
      { id: "knowledge", to: "/support/connaissances", label: "Base de connaissance", icon: BookOpen, testid: "nav-knowledge", visible: canReadKnowledge(user) },
      // Until the module is switched on, administrators still see it as planned.
      ...(on("governance") ? [] : [soon("knowledge", "Base de connaissance", BookOpen)]),
    ] },
    // Produit (Ordre 19): items and roadmap, releases, incidents. A submit-only account has no list to
    // read: its entry opens the form.
    { id: "product", label: "Produit", icon: ProductIcon, accent: "violet", description: "Retours, feuille de route, versions et incidents.", items: [
      { id: "feedback", to: "/produit", except: ["/produit/versions", "/produit/incidents"], label: "Retours et feuille de route", icon: Lightbulb, testid: "nav-product",
        visible: on("product") && can(PRODUCT_READ_KEYS) },
      { id: "feedback-new", to: "/produit/nouveau", label: "Signaler un retour", icon: Lightbulb, testid: "nav-product-new",
        visible: on("product") && !can(PRODUCT_READ_KEYS) && can(PRODUCT_CREATE_KEYS) },
      { id: "releases", to: "/produit/versions", label: "Versions livrées", icon: Rocket, testid: "nav-product-releases", visible: on("product") && can(PRODUCT_READ_KEYS) },
      { id: "incidents", to: "/produit/incidents", label: "Incidents de production", icon: AlertTriangle, testid: "nav-product-incidents", visible: on("product") && can(PRODUCT_READ_KEYS) },
    ] },
    // Documents et contrats (Ordre 18): the register, its obligations (deadlines), the company file (18-3) and the templates (18-4).
    { id: "documents", label: "Documents et contrats", icon: DocumentsIcon, accent: "indigo", description: "Registre, obligations et modèles.", items: [
      { id: "records-all-files", to: "/documents/fichiers", label: "Tous les fichiers", icon: FolderOpen, testid: "nav-records-all-files",
        visible: canSeeRecords(user) },
      { id: "records", to: "/documents", except: ["/documents/obligations", "/documents/fichiers", "/documents/constitution", "/documents/modeles", "/documents/generer", "/documents/charte", "/documents/clauses"],
        label: "Registre", icon: FileText,
        testid: "nav-records", visible: canSeeRecords(user) },
      { id: "records-obligations", to: "/documents/obligations", label: "Obligations", icon: CalendarClock, testid: "nav-records-obligations",
        visible: canSeeRecords(user) },
      { id: "records-constitution", to: "/documents/constitution", label: "Dossier de constitution", icon: FolderCheck,
        testid: "nav-records-constitution", visible: canSeeRecords(user) },
      { id: "records-templates", to: "/documents/modeles", also: ["/documents/generer"], label: "Modèles", icon: FileStack,
        testid: "nav-records-templates", visible: canSeeRecords(user) },
      { id: "records-clauses", to: "/documents/clauses", label: "Clauses", icon: BookText, testid: "nav-records-clauses", visible: canSeeRecords(user) },
      { id: "records-brand", to: "/documents/charte", label: "Charte graphique", icon: Palette, testid: "nav-records-brand",
        visible: canSeeRecords(user) && hasPermission(user, "records:manage_templates") },
    ] },
    { id: "field", label: "Opérations terrain", icon: FieldIcon, accent: "sky", description: "Missions, matériel, formations et équipe.", items: [
      { id: "missions", to: missionsReadable ? "/missions" : "/missions/procedures", label: "Missions", icon: MapPinned, testid: "nav-missions",
        visible: missionsReadable || canManageProcedures(user) },
      { id: "equipment", to: "/equipements", label: "Matériel", icon: Printer, testid: "nav-equipment", visible: can(EQUIPMENT_READ_KEYS) && on("equipment") },
      { id: "training", to: "/formations", label: "Formations", icon: GraduationCap, testid: "nav-training", visible: can(CATALOG_READ_KEYS) && on("training") },
    ] },
    // Collaborateurs: the directory, and the referentials of the team under « Organisation » (sub-menu).
    { id: "team", label: "Collaborateurs", icon: TeamIcon, accent: "cyan", description: "Annuaire, services, fonctions et compétences de l'équipe.", items: [
      { id: "collaborators", to: "/collaborateurs", label: "Annuaire", icon: UsersRound, testid: "nav-collaborators", except: [ORG_BASE, "/collaborateurs/organigramme"],
        visible: can("collaborators:view") && on("collaborators") },
      // The whole company's chart: its own key (`collaborators:view_org`, never granted by default); reorganising it needs `collaborators:reorganize` too (lot L10).
      { id: "org-chart", to: "/collaborateurs/organigramme", label: "Organigramme", icon: Network, testid: "nav-org-chart", visible: can("collaborators:view_org") && on("collaborators") },
      ...visibleOrgSections(user, can).map((section) => ({
        id: `org-${section.slug}`, group: "Organisation", groupIcon: Building2, to: orgPath(section.slug), label: section.label, testid: section.testid,
        icon: ORG_ICONS[section.slug], visible: can("collaborators:view") && on("collaborators") })),
    ] },
    // Achats et actifs (Ordre 20): the inventory, the suppliers, and « Mon matériel » for whoever holds something.
    { id: "purchases", label: "Achats et actifs", icon: PurchasesIcon, accent: "rose", description: "Demandes d'achat, fournisseurs et matériel de l'entreprise.", items: [
      { id: "requests", to: "/achats/demandes", label: "Demandes d'achat", icon: ShoppingCart, testid: "nav-requests", visible: canSeeRequests(user) },
      { id: "assets", to: "/achats/actifs", label: "Actifs", icon: Laptop, testid: "nav-assets", visible: canSeeInventory(user) },
      { id: "inventory", to: "/achats/inventaire", label: "Inventaire", icon: ClipboardList, testid: "nav-inventory", visible: canSeeInventory(user) },
      { id: "suppliers", to: "/achats/fournisseurs", label: "Fournisseurs", icon: Truck, testid: "nav-suppliers", visible: canSeeSuppliers(user) },
      { id: "my-assets", to: "/achats/mon-materiel", label: "Mon matériel", icon: PackageCheck, testid: "nav-my-assets", visible: canSeeOwnAssets(user) },
    ] },
    { id: "finance", label: "Finances internes", icon: FinanceIcon, accent: "lime", description: "Dépenses, notes de frais et remboursements.", items: [
      { id: "finance", to: "/finance", label: "Finances internes", icon: Wallet, testid: "nav-finance", visible: financeVisible },
    ] },
    // Settings open to whoever may see at least one of its sections, each section keeping its own permission.
    { id: "admin", label: "Administration", icon: AdminIcon, accent: "slate", description: "Paramètres, utilisateurs, rôles, journal, corbeille et cartographie du système.", items: [
      { id: "settings", to: "/settings", label: "Paramètres", icon: SettingsIcon, testid: "nav-settings", visible: can(SETTINGS_ACCESS) },
      { id: "users", to: "/users", label: "Utilisateurs", icon: UserCog, testid: "nav-users", visible: can("users:view") },
      { id: "roles", to: "/roles", label: "Rôles & permissions", icon: Lock, testid: "nav-roles", visible: isAdmin },
      { id: "activity", to: "/activity", label: "Journal d'activité", icon: History, testid: "nav-activity", visible: can("activity:view") },
      { id: "trash", to: "/corbeille", label: "Corbeille", icon: Trash2, testid: "nav-trash", visible: can(TRASH_ACCESS) },
      { id: "architecture", to: "/architecture", label: "Cartographie du système", icon: Network, testid: "nav-architecture", visible: on("architecture") && can("architecture:view") },
    ] },
    // Jamora App, formerly « Administration Jamora » (Ordre 27; same routes and rights): the platform and its pharmacies, read only, behind a short re-authentication.
    { id: "jamora-admin", label: "Jamora App", icon: JamoraAppIcon, accent: "brand", description: "Pharmacies, démos et diagnostics de la plateforme (lecture seule).", items: [
      { id: "jamora-admin-overview", to: JAMORA_ADMIN_BASE, except: [`${JAMORA_ADMIN_BASE}/pharmacies`, `${JAMORA_ADMIN_BASE}/demandes-de-demo`, `${JAMORA_ADMIN_BASE}/impression`, `${JAMORA_ADMIN_BASE}/onepharma`, `${JAMORA_ADMIN_BASE}/comptes`, `${JAMORA_ADMIN_BASE}/catalogues`, `${JAMORA_ADMIN_BASE}/journaux`, `${JAMORA_ADMIN_BASE}/p`],
        label: "Vue d'ensemble", icon: LayoutDashboard, testid: "nav-jamora-admin", visible: canOpenJamoraAdmin(user) && canViewJamoraPlatform(user) },
      { id: "jamora-admin-pharmacies", to: `${JAMORA_ADMIN_BASE}/pharmacies`, also: [`${JAMORA_ADMIN_BASE}/p`], label: "Pharmacies", icon: Store, testid: "nav-jamora-admin-pharmacies", visible: canViewJamoraPlatform(user) },
      { id: "jamora-admin-demo", to: `${JAMORA_ADMIN_BASE}/demandes-de-demo`, label: "Demandes de démo", icon: Inbox, testid: "nav-jamora-admin-demo", visible: canViewJamoraPlatform(user) },
      { id: "jamora-admin-printing", to: `${JAMORA_ADMIN_BASE}/impression`, label: "Postes d'impression", icon: Printer, testid: "nav-jamora-admin-printing", visible: canViewJamoraPlatform(user) },
      { id: "jamora-admin-onepharma", to: `${JAMORA_ADMIN_BASE}/onepharma`, label: "Synchronisations Onepharma", icon: Database, testid: "nav-jamora-admin-onepharma", visible: canViewJamoraPlatform(user) },
      { id: "jamora-admin-users", to: `${JAMORA_ADMIN_BASE}/comptes`, label: "Comptes", icon: Users, testid: "nav-jamora-admin-users", visible: canViewJamoraPlatform(user) },
      { id: "jamora-admin-catalogues", to: `${JAMORA_ADMIN_BASE}/catalogues`, label: "Catalogues", icon: Library, testid: "nav-jamora-admin-catalogues", visible: canViewJamoraPlatform(user) },
      { id: "jamora-admin-logs", to: `${JAMORA_ADMIN_BASE}/journaux`, label: "Journaux", icon: ScrollText, testid: "nav-jamora-admin-logs", visible: canViewJamoraLogs(user) },
      { id: "jamora-admin-pharmacy", to: `${JAMORA_ADMIN_BASE}/p`, label: "Fiche d'une pharmacie", icon: Building2, testid: "nav-jamora-admin-pharmacy",
        visible: canViewJamoraPharmacy(user) && !canViewJamoraPlatform(user) },
    ] },
    { id: "marketing", label: "Marketing et partenariats", icon: MarketingIcon, accent: "pink", description: "Campagnes et partenaires.", items: [
      { id: "campaigns", to: "/marketing/campagnes", label: "Campagnes", icon: Megaphone, testid: "nav-campaigns", visible: canSeeMarketing(user) },
      { id: "partners", to: "/marketing/partenaires", label: "Partenaires", icon: Handshake, testid: "nav-partners", visible: canSeeMarketing(user) },
      { id: "marketing-indicators", to: "/marketing/indicateurs", label: "Indicateurs", icon: BarChart3, testid: "nav-marketing-indicators", visible: canSeeMarketing(user) },
      // Until the module is switched on, administrators still see it as planned.
      ...(on("marketing") ? [] : [soon("marketing", "Marketing et partenariats", Megaphone)]),
    ] },
    { id: "governance", label: "Gouvernance", icon: GovernanceIcon, accent: "amber", description: "Décisions et pilotage de la société.", items: [
      { id: "decisions", to: "/gouvernance/decisions", label: "Décisions", icon: Gavel, testid: "nav-decisions", visible: canSeeGovernance(user) },
      { id: "meetings", to: "/gouvernance/reunions", label: "Réunions", icon: CalendarDays, testid: "nav-meetings", visible: canSeeGovernance(user) },
      { id: "risks", to: "/gouvernance/risques", label: "Risques", icon: ShieldAlert, testid: "nav-risks", visible: canSeeGovernance(user) },
      // Until the module is switched on, administrators still see it as planned.
      ...(on("governance") ? [] : [soon("governance", "Gouvernance", Landmark)]),
    ] },
  ];
  const apps = buildApps(sections);
  const globalItems = sections.find((section) => section.flat).items.filter((item) => item.visible);
  // Copilote and Cockpit are tools rather than applications: they get a tile of their own in the centre.
  const toolDescriptions = { copilot: "Posez vos questions à l'assistant IA.", cockpit: "Vue de direction de la société." };
  const toolAccents = { copilot: "fuchsia", cockpit: "slate" };
  const toolIcons = { copilot: CopilotLogo, cockpit: CockpitIcon };
  const tools = globalItems.filter((item) => toolDescriptions[item.id])
    .map((item) => ({ ...item, icon: toolIcons[item.id], description: toolDescriptions[item.id], accent: toolAccents[item.id], items: [], upcoming: !!item.upcoming, to: item.upcoming ? null : item.to }));
  const currentAppId = pathname === APPS_PAGE ? null : appIdOfPath(apps, pathname);
  const currentApp = apps.find((app) => app.id === currentAppId) || null;
  const breadcrumb = breadcrumbOf(apps, globalItems, pathname, APPS_PAGE);

  const memory = useNavMemory();
  const favoriteApps = memory.favorites.map((id) => apps.find((app) => app.id === id)).filter((app) => app && !app.upcoming);
  const settingsItem = apps.find((app) => app.id === "admin")?.items.find((item) => item.id === "settings") || null;
  const index = entryIndex(apps, globalItems);
  const recents = resolveRecents(memory.recents, index).map((recent) => ({ ...recent, appLabel: apps.find((app) => app.id === recent.appId)?.label }));
  // A page counts as visited once it has been read for a moment (not when merely crossed), and the dashboard and the centre
  // are not « recent » pages. The time kept is the one of the visit.
  const openedId = pathname === APPS_PAGE ? null
    : [...index.values()].find(({ item }) => item.id !== "dashboard" && matchesPath(item, pathname))?.item.id;
  const { remember } = memory;
  useEffect(() => {
    if (!openedId) return undefined;
    const visitedAt = Date.now();
    const timer = window.setTimeout(() => remember(openedId, visitedAt), VISIT_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [openedId, remember]);

  const counts = useAppCounts(apps, pathname === APPS_PAGE);
  const navContext = { apps, tools, counts, recents, favorites: memory.favorites, toggleFavorite: memory.toggleFavorite };

  return (
    <CopilotProvider>
    <div className="relative h-screen flex bg-slate-50 overflow-clip">
      {/* Sidebar: computer only; phones and tablets use the fixed bottom nav instead */}
      {/* The logo stays put and the menu scrolls under it: on a short screen the last entries stay reachable. */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-jamora text-white flex-shrink-0 min-h-0 [--jamora-icon-navy:#ffffff]">
        <div className="p-5 border-b border-white/10 flex items-center justify-center shrink-0">
          <NavLink to="/dashboard" aria-label="Tableau de bord" data-testid="logo-home"><img src={logo} alt="Jamora CRM" className="h-14 w-auto object-contain" /></NavLink>
        </div>
        <nav className="p-3 space-y-1 flex-1 min-h-0 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.25)_transparent]"
          data-testid="sidebar-nav">
          <TooltipProvider delayDuration={150}>
            {currentApp ? (
              <>
                <NavLink to={APPS_PAGE} data-testid="nav-all-apps" className={launcherClass(false)}>
                  <ArrowLeft size={18} aria-hidden="true" />
                  <span className="flex-1">Toutes les applications</span>
                </NavLink>
                <div className="px-3 pb-2 flex items-center gap-2 text-sm font-semibold text-white" data-testid="nav-current-app">
                  <currentApp.icon size={18} aria-hidden="true" />
                  {currentApp.label}
                </div>
                <div className="ml-5 pl-2 border-l border-white/15 space-y-1" data-testid="nav-app-items">
                  <NavItems items={currentApp.items} />
                </div>
              </>
            ) : (
              <>
                {/* The launcher is the way into the whole CRM, not a page among others: filled button above the pages. */}
                <NavLink to={APPS_PAGE} data-testid="nav-apps" className={({ isActive }) => launcherClass(isActive)}>
                  <LauncherIcon size={18} />
                  <span className="flex-1">Applications</span>
                  <ChevronRight size={16} className="opacity-70" aria-hidden="true" />
                </NavLink>
                <div className="space-y-1 pt-2">
                  {globalItems.filter((item) => ["dashboard", "cockpit", "tasks", "copilot"].includes(item.id)).map((item) => <NavEntry key={item.id} item={item} />)}
                </div>
                <div className="pt-3" data-testid="nav-favorites">
                  <div className="flex items-center gap-2 px-3 pb-1 text-2xs font-semibold uppercase tracking-wider text-teal-400/80">
                    <Star size={12} aria-hidden="true" /> Favoris
                  </div>
                  {favoriteApps.length === 0
                    ? <p className="px-3 py-1 text-xs text-white/40">Étoilez une application pour la retrouver ici.</p>
                    : <div className="space-y-1">{favoriteApps.map((app) => <AppLink key={app.id} app={app} />)}</div>}
                </div>
              </>
            )}
          </TooltipProvider>
        </nav>
        <div className="p-3 border-t border-white/10 shrink-0 space-y-1">
          {settingsItem && currentApp?.id !== "admin" && <NavEntry item={settingsItem} />}  {/* inside « Administration » it is already one of the entries */}
          <InstallButton testid="sidebar-install" className={`w-full ${navLinkClass({ isActive: false })}`} />
          <VersionBadge className="block px-3 pt-1" testid="sidebar-version" />
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 flex flex-col min-w-0 ${fullBleed ? "overflow-hidden" : "overflow-y-auto"}`}>
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-200">
          {/* On a computer: breadcrumb left, search centred (the two side columns are equal), actions right. Below lg the search fills the row. */}
          <div className="flex items-center gap-3 px-4 md:px-6 py-3 lg:grid lg:grid-cols-[1fr_minmax(0,36rem)_1fr]">
            <div className="hidden lg:flex min-w-0 items-center">
              {breadcrumb.length > 0 && (
                <nav aria-label="Fil d'Ariane" data-testid="breadcrumb" className="flex min-w-0 items-center gap-1.5 text-sm text-slate-500">
                  {breadcrumb.map((crumb, i) => {
                    const last = i === breadcrumb.length - 1;
                    return (
                      <Fragment key={`${crumb.kind}-${crumb.to}`}>
                        {i > 0 && <ChevronRight size={14} className="shrink-0 text-slate-400" aria-hidden="true" />}
                        {/* The centre always shows as its icon (user's request 2026-10-23); its name stays for screen readers and as a tooltip. */}
                        <Link to={crumb.to} className={`${crumb.kind === "apps" && !last ? "inline-flex shrink-0 items-center gap-1.5" : "truncate"} ${last ? "font-medium text-slate-900" : "hover:text-slate-800"}`}
                          aria-current={last ? "page" : undefined} title={crumb.kind === "apps" && !last ? crumb.label : undefined} data-testid={`breadcrumb-${crumb.kind}`}>
                          {crumb.kind === "apps" && !last ? (
                            <>
                              <LauncherIcon size={15} aria-hidden="true" />
                              <span className="sr-only">{crumb.label}</span>
                            </>
                          ) : crumb.label}
                        </Link>
                      </Fragment>
                    );
                  })}
                </nav>
              )}
            </div>
            <div className="flex min-w-0 flex-1 lg:flex-none"><GlobalSearch /></div>
            <div className="flex items-center gap-2 ml-auto lg:justify-end">
              <Link to={APPS_PAGE} aria-label="Applications" title="Applications" data-testid="btn-apps"
                className="hidden lg:flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100">
                <LauncherIcon size={20} aria-hidden="true" />
              </Link>
              <QuickActionsMenu />
              <NotificationsBell />
              <UserMenu />
            </div>
          </div>
        </header>
        <OfflineBanner />
        <TimezoneNotice />
        <UpdateBanner />
        <WhatsNewDialog />
        <main className={fullBleed ? "flex min-h-0 flex-1 pb-16 lg:pb-0" : "flex-1 p-4 md:p-6 pb-20 md:pb-24 lg:pb-6 overflow-x-clip"}>
          <PageErrorBoundary key={pathname}>
            <Outlet context={navContext} />
          </PageErrorBoundary>
        </main>
      </div>

      <MobileBottomNav globalItems={globalItems} currentApp={currentApp} recents={recents} />
      <Copilot />
    </div>
    </CopilotProvider>
  );
}
